import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Newspaper, Globe, AlertCircle, CheckCircle, Clock, Tag, Zap } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { validateNewsOptions, getUrgencyColor, getCategoryColor } from './beritaUtils'

const BeritaOutlinePage = ({ 
  query, 
  beritaOptions, 
  onStartGeneration, 
  onBack, 
  llmService, 
  isLoading,
  language,
  onLanguageChange
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(language)

  const handleLanguageChange = (newLanguage) => {
    setSelectedLanguage(newLanguage)
    onLanguageChange(newLanguage)
  }

  const handleCreateNews = (option) => {
    onStartGeneration(option)
  }

  // Show loading state while generating initial options
  if (isLoading && beritaOptions.length === 0) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-text-secondary">
            {language === 'id' ? 'Mencari opsi berita terbaru...' : 'Searching for latest news options...'}
          </p>
        </div>
      </div>
    )
  }

  const validation = validateNewsOptions(beritaOptions)

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border-primary sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-3 py-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">Kembali</span>
              </button>
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="p-2 bg-red-500/10 rounded-lg flex-shrink-0">
                  <Newspaper className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl font-semibold text-text-primary truncate">
                    {language === 'id' ? 'Berita Agent' : 'News Agent'}
                  </h1>
                  <p className="text-xs sm:text-sm text-text-tertiary truncate">
                    {language === 'id' ? 'Pilih Opsi Berita' : 'Select News Option'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center justify-end sm:justify-start">
              <div className="flex items-center space-x-1 sm:space-x-2 bg-bg-tertiary rounded-lg p-1">
                <button
                  onClick={() => handleLanguageChange('id')}
                  className={cn(
                    "px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium transition-colors",
                    selectedLanguage === 'id'
                      ? "bg-red-500 text-white"
                      : "text-text-secondary hover:text-text-primary"
                  )}
                >
                  🇮🇩 ID
                </button>
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={cn(
                    "px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium transition-colors",
                    selectedLanguage === 'en'
                      ? "bg-red-500 text-white"
                      : "text-text-secondary hover:text-text-primary"
                  )}
                >
                  🇺🇸 EN
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Topic Display */}
        <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            {language === 'id' ? 'Topik Berita' : 'News Topic'}
          </h2>
          <p className="text-text-secondary text-lg">"{query}"</p>
          <div className="flex items-center space-x-2 mt-3">
            <Globe className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm text-text-tertiary">
              {language === 'id' ? 'Bahasa: Indonesia' : 'Language: English'}
            </span>
          </div>
        </div>

        {/* Validation Message */}
        {!validation.isValid && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-500 font-medium">{validation.message}</p>
            </div>
          </div>
        )}

        {/* News Options */}
        {validation.isValid && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-text-primary">
                {language === 'id' ? 'Pilih Opsi Berita' : 'Choose News Option'}
              </h2>
              <div className="text-sm text-text-tertiary">
                {beritaOptions.length} {language === 'id' ? 'opsi tersedia' : 'options available'}
              </div>
            </div>

            <div className="grid gap-6">
              {beritaOptions.map((option, index) => (
                <div key={option.id} className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden hover:border-red-500/30 transition-colors">
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                          <span className="text-sm font-medium text-text-tertiary bg-bg-tertiary px-2 py-1 rounded flex-shrink-0">
                            #{index + 1}
                          </span>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={cn(
                              "text-xs px-2 py-1 rounded font-medium flex items-center flex-shrink-0",
                              getCategoryColor(option.category)
                            )}>
                              <Tag className="w-3 h-3 mr-1" />
                              <span className="truncate">{option.category}</span>
                            </span>
                            <span className={cn(
                              "text-xs px-2 py-1 rounded font-medium flex items-center flex-shrink-0",
                              getUrgencyColor(option.urgency)
                            )}>
                              <Zap className="w-3 h-3 mr-1" />
                              <span className="truncate">
                                {option.urgency === 'high' ? (language === 'id' ? 'Tinggi' : 'High') :
                                 option.urgency === 'medium' ? (language === 'id' ? 'Sedang' : 'Medium') :
                                 (language === 'id' ? 'Rendah' : 'Low')}
                              </span>
                            </span>
                          </div>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-text-primary mb-2 break-words">
                          {option.title}
                        </h3>
                        
                        <div className="mb-3">
                          <p className="text-sm font-medium text-text-tertiary mb-1">
                            {language === 'id' ? 'Sudut Pandang:' : 'Angle:'}
                          </p>
                          <p className="text-text-secondary break-words">{option.angle}</p>
                        </div>
                        
                        <div className="mb-4">
                          <p className="text-sm font-medium text-text-tertiary mb-1">
                            {language === 'id' ? 'Deskripsi:' : 'Description:'}
                          </p>
                          <p className="text-text-secondary break-words">{option.description}</p>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-text-tertiary">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{option.estimatedLength}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0 w-full sm:w-auto">
                        <button
                          onClick={() => handleCreateNews(option)}
                          className={cn(buttonVariants.primary, "flex items-center justify-center text-center min-w-0 w-full sm:w-auto")}
                        >
                          <Newspaper className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{language === 'id' ? 'Buat Berita' : 'Create News'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Info Section */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mt-8">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <h3 className="font-medium text-blue-500 mb-1">
                    {language === 'id' ? 'Informasi Penting' : 'Important Information'}
                  </h3>
                  <ul className="text-sm text-blue-400 space-y-1">
                    <li>• {language === 'id' ? 'Setiap berita akan dibuat dengan referensi dari 10-20 sumber terpercaya' : 'Each news article will be created with references from 10-20 trusted sources'}</li>
                    <li>• {language === 'id' ? 'Menggunakan kaidah jurnalistik yang baik dan benar' : 'Following proper journalistic standards'}</li>
                    <li>• {language === 'id' ? 'Fokus pada berita terbaru dan aktual' : 'Focus on latest and current news'}</li>
                    <li>• {language === 'id' ? 'Konten original dan tidak plagiat' : 'Original content, no plagiarism'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

BeritaOutlinePage.propTypes = {
  query: PropTypes.string.isRequired,
  beritaOptions: PropTypes.array.isRequired,
  onStartGeneration: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  language: PropTypes.string.isRequired,
  onLanguageChange: PropTypes.func.isRequired
}

export default BeritaOutlinePage